'use client'

/**
 * useImagePasteUpload (Issue #9859)
 *
 * Reusable hook that extracts the paste-image-upload logic from
 * feedback-modal.tsx so any component supporting clipboard-image input
 * (e.g. feedback-modal, pulse-chat) can share the same behaviour.
 *
 * Provides:
 * - `onPaste` — React.ClipboardEventHandler that detects image/* clipboard
 *   items, builds a timestamped File, and uploads it via the attachment
 *   server actions.
 * - `attachments` — uploaded attachment objects ready for submission.
 * - `isUploading` — true while an upload is in flight.
 * - `error` — most recent upload error string, or null.
 * - `removeAttachment(id)` — removes an attachment from local state.
 * - `clearAttachments()` — resets state (e.g. after form submit).
 * - `restoreAttachments(list)` — puts back an already-uploaded list (#14531).
 */

import { useState, useCallback } from 'react'
import type React from 'react'
import { toastError } from '@/lib/toast'
import { createUploadUrl, uploadFileToStorage } from '@/app/actions/attachments'
import {
  resolveFileMimeType,
  getMaxSizeForType,
  getSupportedTypesDescription,
} from '@/lib/storage/file-types'
import { isPreviewableImage } from '@/lib/file-icons'
import {
  HeicConversionError,
  normalizeImageFile,
} from '@/lib/attachments/convert-heic'
import { isHeicFile } from '@/lib/attachments/heic'
import type { ValidAttachableType } from '@/lib/attachment-constants'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UploadedAttachment {
  id: string
  fileName: string
  mimeType: string
  sizeBytes: number
  /** Base64 data-URL preview — only populated for previewable image types. */
  preview?: string
}

export interface UseImagePasteUploadConfig {
  /** The entity type this attachment will be associated with. */
  attachableType: ValidAttachableType
  /**
   * The entity ID this attachment will be associated with. When the real
   * entity ID is not yet known (e.g. an unsaved record), pass a temporary
   * placeholder string — the attachment record uses whatever is supplied.
   */
  attachableId: string
  /**
   * Maximum number of attachments allowed. Defaults to 5, matching the
   * MAX_ATTACHMENTS constant used in feedback-modal.tsx.
   */
  maxAttachments?: number
  /**
   * Number of additional attachment slots already consumed outside the hook
   * (e.g. a screenshot tracked in separate state). The hook adds this to
   * `attachments.length` when enforcing the cap so callers that track some
   * attachments externally don't accidentally allow more than `maxAttachments`
   * total. Defaults to 0.
   */
  reservedSlots?: number
}

export interface UseImagePasteUploadReturn {
  attachments: UploadedAttachment[]
  isUploading: boolean
  error: string | null
  onPaste: React.ClipboardEventHandler
  /**
   * Upload an arbitrary File as an attachment. Useful for file-select flows
   * that share the same attachment list as paste-image uploads.
   */
  uploadFile: (file: File) => Promise<void>
  removeAttachment: (id: string) => void
  clearAttachments: () => void
  /**
   * Replace the list with already-uploaded attachments, without uploading
   * anything. The Undo half of the feedback modal's "Start over" (#14531).
   */
  restoreAttachments: (list: readonly UploadedAttachment[]) => void
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const DEFAULT_MAX_ATTACHMENTS = 5

export function useImagePasteUpload(
  config: UseImagePasteUploadConfig,
): UseImagePasteUploadReturn {
  const {
    attachableType,
    attachableId,
    maxAttachments = DEFAULT_MAX_ATTACHMENTS,
    reservedSlots = 0,
  } = config

  const [attachments, setAttachments] = useState<UploadedAttachment[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // -------------------------------------------------------------------------
  // Core upload helper (shared by onPaste and any future file-select callers)
  // -------------------------------------------------------------------------
  const uploadFile = useCallback(
    async (chosen: File) => {
      if (attachments.length + reservedSlots >= maxAttachments) {
        setError(`Maximum ${maxAttachments} attachments allowed`)
        return
      }

      setError(null)

      // A HEIC (an iPhone photo) is converted to JPEG before anything is
      // checked or sent (#16099); any other file is used as-is and the
      // decoder never loads.
      let file = chosen
      if (isHeicFile(chosen)) {
        setIsUploading(true)
        try {
          file = await normalizeImageFile(chosen)
        } catch (err) {
          if (!(err instanceof HeicConversionError)) throw err
          setError(err.message)
          toastError({
            message: err.message,
            error: err.cause ?? err,
            context: { action: 'convertHeic' },
          })
          return
        } finally {
          setIsUploading(false)
        }
      }

      const resolvedType = resolveFileMimeType(file.name, file.type)
      if (!resolvedType) {
        setError(
          `Unsupported file type. Supported: ${getSupportedTypesDescription()}`,
        )
        return
      }

      const maxSize = getMaxSizeForType(resolvedType)
      if (file.size > maxSize) {
        const maxMB = maxSize / (1024 * 1024)
        setError(`File size must be less than ${maxMB}MB`)
        return
      }

      setIsUploading(true)

      try {
        const urlResult = await createUploadUrl({
          fileName: file.name,
          mimeType: resolvedType,
          sizeBytes: file.size,
          attachableType,
          attachableId,
        })

        if (!urlResult.success) {
          const urlErrMsg = urlResult.error ?? 'Failed to get upload URL'
          setError(urlErrMsg)
          toastError({
            message: urlErrMsg,
            context: { action: 'createUploadUrl' },
          })
          return
        }
        if (!urlResult.attachmentId) {
          const urlErrMsg = 'Failed to get upload URL'
          setError(urlErrMsg)
          toastError({
            message: urlErrMsg,
            context: { action: 'createUploadUrl' },
          })
          return
        }

        const uploadFormData = new FormData()
        uploadFormData.append('file', file)
        uploadFormData.append('attachmentId', urlResult.attachmentId)

        const uploadResult = await uploadFileToStorage(uploadFormData)

        if (!uploadResult.success) {
          const uploadErrMsg = uploadResult.error ?? 'Failed to upload file'
          setError(uploadErrMsg)
          toastError({
            message: uploadErrMsg,
            context: { action: 'uploadFileToStorage' },
          })
          return
        }

        let preview: string | undefined
        if (isPreviewableImage(resolvedType)) {
          preview = await new Promise<string>(resolve => {
            const reader = new FileReader()
            reader.onload = e => resolve(e.target?.result as string)
            reader.onerror = () => resolve('')
            reader.readAsDataURL(file)
          })
        }

        setAttachments(prev => [
          ...prev,
          {
            id: urlResult.attachmentId as string,
            fileName: file.name,
            mimeType: resolvedType,
            sizeBytes: file.size,
            preview,
          },
        ])
      } catch (err) {
        console.info('[useImagePasteUpload] File upload failed:', err)
        const message = 'Failed to upload file. Please try again.'
        setError(message)
        toastError({ message, context: { action: 'uploadFile' } })
      } finally {
        setIsUploading(false)
      }
    },
    // attachments.length + reservedSlots enforce the cap; include both so the
    // closure always reflects the current totals.
    [
      attachableType,
      attachableId,
      maxAttachments,
      reservedSlots,
      attachments.length,
    ],
  )

  // -------------------------------------------------------------------------
  // Paste handler
  // -------------------------------------------------------------------------
  const onPaste = useCallback<React.ClipboardEventHandler>(
    e => {
      const items = e.clipboardData?.items
      if (!items) return

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const blob = item.getAsFile()
          if (!blob) return

          const ext = blob.type.split('/')[1] ?? 'png'
          const timestamp = new Date()
            .toISOString()
            .replace(/[:.]/g, '-')
            .slice(0, 19)
          const file = new File([blob], `pasted-image-${timestamp}.${ext}`, {
            type: blob.type,
          })

          uploadFile(file)
          return
        }
      }
    },
    [uploadFile],
  )

  // -------------------------------------------------------------------------
  // Remove / clear helpers
  // -------------------------------------------------------------------------
  const removeAttachment = useCallback((id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id))
  }, [])

  const clearAttachments = useCallback(() => {
    setAttachments([])
    setError(null)
  }, [])

  const restoreAttachments = useCallback(
    (list: readonly UploadedAttachment[]) => {
      setAttachments([...list])
      setError(null)
    },
    [],
  )

  return {
    attachments,
    isUploading,
    error,
    onPaste,
    uploadFile,
    removeAttachment,
    clearAttachments,
    restoreAttachments,
  }
}
